// Stefans-AI-CV-frontend/src/stores/appStore.ts

import { create } from 'zustand'
import { devtools } from 'zustand/middleware'

// 类型定义
interface ChatMessage {
  id: string
  content: string
  isUser: boolean
  timestamp: number
  metadata?: {
    responseTime?: number
    confidence?: number
    sourceType?: string
    processingSteps?: string[]
    finalStatus?: string
  }
}

interface ChatSession {
  id: string
  messages: ChatMessage[]
  createdAt: number
  lastActiveAt: number
  messageCount: number
  title?: string
}

interface UserSession {
  cookieId: string
  totalMessageCount: number
  chatSessions: Record<string, ChatSession>
  currentChatId?: string
  createdAt: number
  lastActiveAt: number
  // 每日限制
  dailyMessageCount: number   // 今日用户发出的消息数
  dailyResetDate: string      // 'YYYY-MM-DD'，上次重置日期
}

interface AppState {
  // 用户会话状态
  userSession: UserSession | null
  currentChatId: string | null
  currentMessages: ChatMessage[]

  // UI状态
  isLoading: boolean
  isTyping: boolean
  currentLanguage: 'zh' | 'en'
  selectedGraphNode: any

  // 错误状态
  error: string | null

  // Actions
  initializeSession: () => void
  setLanguage: (lang: 'zh' | 'en') => void
  createNewChat: () => string
  switchToChat: (chatId: string) => void
  deleteChat: (chatId: string) => void
  addMessage: (message: Omit<ChatMessage, 'id' | 'timestamp'>) => void
  setTyping: (isTyping: boolean) => void
  setLoading: (isLoading: boolean) => void
  setError: (error: string | null) => void
  setSelectedGraphNode: (node: any) => void

  // 聊天限制检查
  canSendMessage: () => { canSend: boolean; reason?: string }
  getChatMessageCount: (chatId: string) => number
  getTotalMessageCount: () => number

  // 数据持久化
  saveToStorage: () => void
  loadFromStorage: () => void
  clearAllData: () => void
}

// 每日限制常量
const DAILY_USER_MESSAGE_LIMIT = 20  // 每天用户可发送的消息数
const CHAT_MESSAGE_LIMIT = 20        // 单个对话最大消息数（含AI回复）

// 获取今日日期字符串 YYYY-MM-DD（本地时间）
const getTodayString = (): string => {
  const now = new Date()
  const y = now.getFullYear()
  const m = String(now.getMonth() + 1).padStart(2, '0')
  const d = String(now.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

// ID工具函数
const generateId = (): string => {
  // 使用时间戳 + 随机数 + 计数器的组合，确保唯一性
  const timestamp = Date.now().toString(36)
  const randomPart = Math.random().toString(36).substr(2, 9)
  const counterPart = Math.floor(Math.random() * 1000).toString(36)

  return `${timestamp}-${randomPart}-${counterPart}`
}

const getCookieId = (): string => {
  let cookieId = localStorage.getItem('stefans_cookie_id')
  if (!cookieId) {
    cookieId = generateId()
    localStorage.setItem('stefans_cookie_id', cookieId)
    console.log('🆔 Generated new cookie ID:', cookieId.slice(0, 8) + '...')
  }
  return cookieId
}

// Zustand Store
export const useAppStore = create<AppState>()(
  devtools(
    (set, get) => ({
      // 初始状态
      userSession: null,
      currentChatId: null,
      currentMessages: [],
      isLoading: false,
      isTyping: false,
      currentLanguage: 'zh',
      selectedGraphNode: null,
      error: null,

      // 初始化用户会话
      initializeSession: () => {
        console.log('🚀 initializeSession 开始执行')

        // 🔧 检查是否已经初始化过，避免重复初始化
        const currentState = get()
        if (currentState.userSession && currentState.currentChatId) {
          console.log('✅ 会话已存在，跳过重复初始化:', {
            currentChatId: currentState.currentChatId.slice(0, 8) + '...',
            messagesCount: currentState.currentMessages.length,
          })
          return
        }

        const cookieId = getCookieId()
        console.log('🍪 使用 cookieId:', cookieId.slice(0, 8) + '...')

        const savedSession = localStorage.getItem(`user_session_${cookieId}`)
        console.log('💾 localStorage 数据存在:', !!savedSession)

        let userSession: UserSession

        if (savedSession) {
          try {
            // 加载已有会话
            userSession = JSON.parse(savedSession)
            userSession.lastActiveAt = Date.now()
            // 兼容旧数据：补充每日限制字段
            if (userSession.dailyMessageCount === undefined) {
              userSession.dailyMessageCount = 0
              userSession.dailyResetDate = getTodayString()
            }

            console.log('📖 成功加载已有会话:', {
              cookieId: cookieId.slice(0, 8) + '...',
              totalMessages: userSession.totalMessageCount,
              chatCount: Object.keys(userSession.chatSessions).length,
              savedCurrentChatId: userSession.currentChatId?.slice(0, 8) + '...',
            })
          } catch (error) {
            console.error('❌ 解析保存的会话失败:', error)
            // 如果解析失败，创建新会话
            userSession = {
              cookieId,
              totalMessageCount: 0,
              chatSessions: {},
              createdAt: Date.now(),
              lastActiveAt: Date.now(),
              dailyMessageCount: 0,
              dailyResetDate: getTodayString(),
            }
            console.log('🆕 解析失败，创建新会话')
          }
        } else {
          // 创建新会话
          userSession = {
            cookieId,
            totalMessageCount: 0,
            chatSessions: {},
            createdAt: Date.now(),
            lastActiveAt: Date.now(),
            dailyMessageCount: 0,
            dailyResetDate: getTodayString(),
          }
          console.log('🆕 localStorage为空，创建全新会话')
        }

        // 🔧 关键逻辑：确保有可用的聊天会话
        let currentChatId = userSession.currentChatId

        console.log('🔍 检查当前聊天会话:', {
          hasChatId: !!currentChatId,
          chatIdValue: currentChatId?.slice(0, 8) + '...',
          sessionExists: currentChatId ? !!userSession.chatSessions[currentChatId] : false,
          totalSessions: Object.keys(userSession.chatSessions).length,
        })

        // 如果没有当前聊天ID，或者该聊天会话不存在
        if (!currentChatId || !userSession.chatSessions[currentChatId]) {
          console.log('❗ 需要创建新的聊天会话')

          // 🔧 直接在这里创建新聊天，不调用 createNewChat() 避免状态冲突
          const newChatId = generateId()
          console.log('💬 生成新聊天ID:', newChatId.slice(0, 8) + '...')

          const newChatSession: ChatSession = {
            id: newChatId,
            messages: [],
            createdAt: Date.now(),
            lastActiveAt: Date.now(),
            messageCount: 0,
          }

          // 更新用户会话
          userSession.chatSessions[newChatId] = newChatSession
          userSession.currentChatId = newChatId
          currentChatId = newChatId

          console.log('✅ 新聊天会话已创建并添加到用户会话')
        } else {
          console.log('✅ 使用现有聊天会话:', currentChatId.slice(0, 8) + '...')
        }

        // 获取当前消息
        const currentMessages = userSession.chatSessions[currentChatId]?.messages || []
        console.log('📝 当前聊天消息数量:', currentMessages.length)

        // 🔧 一次性更新所有状态
        set({
          userSession,
          currentChatId,
          currentMessages,
        })

        // 保存到本地存储
        try {
          localStorage.setItem(`user_session_${userSession.cookieId}`, JSON.stringify(userSession))
          console.log('💾 会话已保存到 localStorage')
        } catch (error) {
          console.error('❌ 保存会话到 localStorage 失败:', error)
        }

        console.log('✅ initializeSession 完成:', {
          finalChatId: currentChatId.slice(0, 8) + '...',
          finalMessageCount: currentMessages.length,
          totalChats: Object.keys(userSession.chatSessions).length,
        })
      },

      // 设置语言
      setLanguage: (lang) => {
        set({ currentLanguage: lang })
        localStorage.setItem('stefans_language', lang)
        console.log('🌐 Language changed to:', lang)
      },

      // 创建新对话
      createNewChat: () => {
        const { userSession } = get()

        if (!userSession) {
          console.error('❌ 无法创建新聊天 - 没有用户会话')
          // 如果没有用户会话，先初始化
          get().initializeSession()
          return get().currentChatId || generateId()
        }

        const newChatId = generateId()
        console.log('💬 用户主动创建新聊天:', newChatId.slice(0, 8) + '...')

        const newChatSession: ChatSession = {
          id: newChatId,
          messages: [],
          createdAt: Date.now(),
          lastActiveAt: Date.now(),
          messageCount: 0,
        }

        const updatedSession = {
          ...userSession,
          chatSessions: {
            ...userSession.chatSessions,
            [newChatId]: newChatSession,
          },
          currentChatId: newChatId,
          lastActiveAt: Date.now(),
        }

        console.log('🔄 更新会话状态:', {
          newChatId: newChatId.slice(0, 8) + '...',
          totalChats: Object.keys(updatedSession.chatSessions).length,
        })

        set({
          userSession: updatedSession,
          currentChatId: newChatId,
          currentMessages: [],
        })

        // 保存到存储
        get().saveToStorage()

        console.log('✅ 新聊天创建完成:', newChatId.slice(0, 8) + '...')
        return newChatId
      },

      // 切换到指定对话
      switchToChat: (chatId) => {
        const { userSession } = get()
        if (!userSession || !userSession.chatSessions[chatId]) {
          console.error('❌ Chat not found:', chatId)
          return
        }

        console.log('🔄 Switching to chat:', chatId.slice(0, 8) + '...')

        const chatSession = userSession.chatSessions[chatId]
        chatSession.lastActiveAt = Date.now()

        const updatedSession = {
          ...userSession,
          currentChatId: chatId,
          lastActiveAt: Date.now(),
        }

        set({
          userSession: updatedSession,
          currentChatId: chatId,
          currentMessages: chatSession.messages,
        })

        get().saveToStorage()
      },

      // 删除对话
      deleteChat: (chatId) => {
        const { userSession, currentChatId } = get()
        if (!userSession || !userSession.chatSessions[chatId]) {
          console.error('❌ Cannot delete chat - not found:', chatId)
          return
        }

        console.log('🗑️ Deleting chat:', chatId.slice(0, 8) + '...')

        const { [chatId]: deletedChat, ...remainingChats } = userSession.chatSessions

        // 如果删除的是当前聊天，需要切换到其他聊天或创建新聊天
        let newCurrentChatId: string | undefined = currentChatId || undefined
        if (currentChatId === chatId) {
          const remainingChatIds = Object.keys(remainingChats)
          if (remainingChatIds.length > 0) {
            // 切换到最近的聊天
            newCurrentChatId = remainingChatIds.sort((a, b) => remainingChats[b].lastActiveAt - remainingChats[a].lastActiveAt)[0]
          } else {
            // 创建新聊天
            newCurrentChatId = get().createNewChat()
            return // createNewChat 已经处理了状态更新
          }
        }

        const updatedSession = {
          ...userSession,
          chatSessions: remainingChats,
          currentChatId: newCurrentChatId,
          totalMessageCount: Math.max(0, userSession.totalMessageCount - deletedChat.messageCount),
          lastActiveAt: Date.now(),
        }

        // 安全地获取当前消息，确保 newCurrentChatId 存在且不为 undefined
        const currentMessages =
          newCurrentChatId === currentChatId
            ? get().currentMessages
            : (newCurrentChatId && remainingChats[newCurrentChatId]?.messages) || []

        set({
          userSession: updatedSession,
          currentChatId: newCurrentChatId,
          currentMessages,
        })

        get().saveToStorage()
      },

      // 添加消息
      addMessage: (messageData) => {
        const { userSession, currentChatId } = get()
        if (!userSession || !currentChatId) {
          console.error('❌ Cannot add message - no active session or chat')
          return
        }

        const message: ChatMessage = {
          ...messageData,
          id: generateId(),
          timestamp: Date.now(),
        }

        console.log('📝 Adding message:', {
          chatId: currentChatId.slice(0, 8) + '...',
          isUser: message.isUser,
          length: message.content.length,
        })

        const chatSession = userSession.chatSessions[currentChatId]
        if (!chatSession) {
          console.error('❌ Chat session not found:', currentChatId)
          return
        }

        const updatedMessages = [...chatSession.messages, message]
        const updatedChatSession = {
          ...chatSession,
          messages: updatedMessages,
          messageCount: updatedMessages.length,
          lastActiveAt: Date.now(),
          title: updatedMessages.length === 1 && message.isUser ? messageData.content.slice(0, 50) : chatSession.title,
        }

        // 重新计算总消息数
        const totalMessageCount = Object.values({
          ...userSession.chatSessions,
          [currentChatId]: updatedChatSession,
        }).reduce((total, session) => total + session.messageCount, 0)

        // 每日用户消息计数（只计用户发送的消息，日期变化时重置）
        const today = getTodayString()
        const isNewDay = (userSession.dailyResetDate ?? '') !== today
        const currentDailyCount = isNewDay ? 0 : (userSession.dailyMessageCount ?? 0)
        const newDailyCount = messageData.isUser ? currentDailyCount + 1 : currentDailyCount

        const updatedSession = {
          ...userSession,
          chatSessions: {
            ...userSession.chatSessions,
            [currentChatId]: updatedChatSession,
          },
          totalMessageCount,
          lastActiveAt: Date.now(),
          dailyMessageCount: newDailyCount,
          dailyResetDate: today,
        }

        set({
          userSession: updatedSession,
          currentMessages: updatedMessages,
        })

        get().saveToStorage()
      },

      // 检查是否可以发送消息
      canSendMessage: () => {
        const { userSession, currentChatId } = get()

        if (!userSession || !currentChatId) {
          return { canSend: false, reason: 'no_session' }
        }

        const currentChat = userSession.chatSessions[currentChatId]
        if (!currentChat) {
          return { canSend: false, reason: 'no_chat' }
        }

        // 检查当前对话消息限制
        if (currentChat.messageCount >= CHAT_MESSAGE_LIMIT) {
          console.warn('⚠️ Chat message limit reached:', currentChat.messageCount)
          return { canSend: false, reason: 'chat_limit' }
        }

        // 检查每日用户消息限制（如果日期变了，有效计数归零）
        const today = getTodayString()
        const effectiveDailyCount =
          (userSession.dailyResetDate ?? '') === today
            ? (userSession.dailyMessageCount ?? 0)
            : 0

        if (effectiveDailyCount >= DAILY_USER_MESSAGE_LIMIT) {
          console.warn('⚠️ Daily message limit reached:', effectiveDailyCount, 'date:', today)
          return { canSend: false, reason: 'cookie_limit' }
        }

        return { canSend: true }
      },

      // 获取指定聊天的消息数量
      getChatMessageCount: (chatId) => {
        const { userSession } = get()
        if (!userSession || !userSession.chatSessions[chatId]) return 0
        return userSession.chatSessions[chatId].messageCount
      },

      // 获取总消息数量
      getTotalMessageCount: () => {
        const { userSession } = get()
        if (!userSession) return 0
        return userSession.totalMessageCount
      },

      // 设置状态
      setTyping: (isTyping) => set({ isTyping }),
      setLoading: (isLoading) => set({ isLoading }),
      setError: (error) => set({ error }),
      setSelectedGraphNode: (node) => set({ selectedGraphNode: node }),

      // 保存到本地存储
      saveToStorage: () => {
        const { userSession } = get()
        if (userSession) {
          try {
            localStorage.setItem(`user_session_${userSession.cookieId}`, JSON.stringify(userSession))
            console.log('💾 Session saved to storage')
          } catch (error) {
            console.error('❌ Failed to save session to storage:', error)
          }
        }
      },

      // 从本地存储加载
      loadFromStorage: () => {
        try {
          const cookieId = getCookieId()
          const savedSession = localStorage.getItem(`user_session_${cookieId}`)
          const savedLanguage = localStorage.getItem('stefans_language') as 'zh' | 'en' | null

          // Detect browser language for first-time visitors (no saved preference)
          const detectBrowserLanguage = (): 'zh' | 'en' => {
            const browserLang = navigator.language || 'en'
            return browserLang.startsWith('zh') ? 'zh' : 'en'
          }

          const resolvedLanguage: 'zh' | 'en' = savedLanguage || detectBrowserLanguage()

          if (savedSession) {
            const userSession = JSON.parse(savedSession)
            // 兼容旧数据：补充每日限制字段
            if (userSession.dailyMessageCount === undefined) {
              userSession.dailyMessageCount = 0
              userSession.dailyResetDate = getTodayString()
            }
            const currentChatId = userSession.currentChatId
            const currentMessages =
              currentChatId && userSession.chatSessions[currentChatId] ? userSession.chatSessions[currentChatId].messages : []

            set({
              userSession,
              currentChatId,
              currentMessages,
              currentLanguage: resolvedLanguage,
            })

            console.log('📖 Session loaded from storage')
          } else {
            set({
              currentLanguage: resolvedLanguage,
            })
          }
        } catch (error) {
          console.error('❌ Failed to load session from storage:', error)
        }
      },

      // 清空所有数据
      clearAllData: () => {
        console.log('🧹 Clearing all data...')

        const cookieId = getCookieId()

        try {
          localStorage.removeItem(`user_session_${cookieId}`)
          localStorage.removeItem('stefans_cookie_id')
          localStorage.removeItem('stefans_language')

          set({
            userSession: null,
            currentChatId: null,
            currentMessages: [],
            isLoading: false,
            isTyping: false,
            error: null,
          })

          // 重新初始化
          setTimeout(() => {
            get().initializeSession()
          }, 100)

          console.log('🧹 All data cleared and reinitialized')
        } catch (error) {
          console.error('❌ Failed to clear data:', error)
        }
      },
    }),
    {
      name: 'stefans-app-store',
    }
  )
)

// 选择器hooks
export const useUserSession = () => useAppStore((state) => state.userSession)

export const useCurrentChat = () =>
  useAppStore((state) => ({
    chatId: state.currentChatId,
    messages: state.currentMessages,
  }))

export const useAppLanguage = () => useAppStore((state) => state.currentLanguage)

export const useAppStatus = () =>
  useAppStore((state) => ({
    isLoading: state.isLoading,
    isTyping: state.isTyping,
    error: state.error,
  }))

// 获取会话统计信息的hook
export const useSessionStats = () =>
  useAppStore((state) => {
    if (!state.userSession) {
      return {
        totalChats: 0,
        totalMessages: 0,
        currentChatMessages: 0,
        canSendMessage: false,
      }
    }

    const currentChatMessages = state.currentChatId ? state.userSession.chatSessions[state.currentChatId]?.messageCount || 0 : 0

    return {
      totalChats: Object.keys(state.userSession.chatSessions).length,
      totalMessages: state.userSession.totalMessageCount,
      currentChatMessages,
      canSendMessage: state.canSendMessage().canSend,
    }
  })

// 工具函数导出
export { generateId, getCookieId }

// 类型导出
export type { ChatMessage, ChatSession, UserSession, AppState }
